/**
 * MfaGate — the mandatory second factor between sign-in and the console.
 *
 * Two modes, chosen from `platformUser.mfa_enabled`:
 *
 *  - ENROLLMENT (mfa_enabled === false): call `mfa-enroll` to obtain a TOTP
 *    secret + otpauth URL, show both AS TEXT (no QR library), let the user add
 *    the account to their authenticator, then verify the first 6-digit code via
 *    `mfa-verify`. First success flips mfa_enabled on (server-side); we refresh
 *    the platform user and mark the gate passed.
 *
 *  - CHALLENGE (mfa_enabled === true): require a fresh 6-digit code via
 *    `mfa-verify`; on ok mark the gate passed.
 *
 * `mfaPassed` lives only in memory, so a reload always re-challenges.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { Button, Card, Field, useTheme } from '@obrava/ui';
import { CenteredCanvas, SectionTitle } from '../components/layout.js';
import { Banner } from '../components/Banner.js';
import { mfaEnroll, mfaVerify } from '../lib/edge.js';
import { useAuth } from './AuthProvider.js';

const CODE_RE = /^\d{6}$/;

interface EnrollData {
  readonly secret: string;
  readonly otpauthUrl: string;
}

/** A read-only secret/URI row with a copy-to-clipboard affordance. */
function CopyRow(props: {
  readonly label: string;
  readonly value: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();
  const [copied, setCopied] = React.useState(false);

  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(props.value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard may be unavailable; the value is selectable as a fallback.
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.space[2] }}>
      <span
        style={{
          fontFamily: theme.typography.family.sans,
          fontSize: theme.typography.size.footnote,
          fontWeight: theme.typography.weight.medium,
          color: theme.color.textSecondary,
        }}
      >
        {props.label}
      </span>
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: theme.spacing.space[3],
        }}
      >
        <code
          style={{
            flex: 1,
            minWidth: 0,
            overflowWrap: 'anywhere',
            padding: theme.spacing.space[4],
            backgroundColor: theme.color.surfaceSunken,
            color: theme.color.textPrimary,
            border: `1px solid ${theme.color.border}`,
            borderRadius: theme.radii.radius.field,
            fontFamily: theme.typography.family.mono,
            fontSize: theme.typography.size.footnote,
            lineHeight: theme.typography.lineHeight.normal,
          }}
        >
          {props.value}
        </code>
        <Button variant="secondary" onClick={() => void onCopy()}>
          {copied ? t('platformConsole.auth:copied') : t('platformConsole.auth:copy')}
        </Button>
      </div>
    </div>
  );
}

export function MfaGate(): React.ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();
  const { platformUser, markMfaPassed, refreshPlatformUser } = useAuth();

  const enrolling = platformUser ? !platformUser.mfa_enabled : true;

  const [enroll, setEnroll] = React.useState<EnrollData | null>(null);
  const [enrollError, setEnrollError] = React.useState<string | null>(null);
  const [code, setCode] = React.useState('');
  const [verifying, setVerifying] = React.useState(false);
  const [verifyError, setVerifyError] = React.useState<string | null>(null);
  const enrollStartedRef = React.useRef(false);

  // Start enrollment ONCE when the user has no MFA yet. We guard with a ref (NOT
  // `starting`/`enroll` in the deps) and do NOT use a `cancelled` cleanup: under
  // React StrictMode the effect runs twice and the re-render from a state setter
  // would otherwise fire the cleanup and discard the in-flight mfa-enroll
  // response — leaving the screen stuck on "Carregant…". The ref survives the
  // double-invoke, so exactly one secret is requested and its response sticks.
  React.useEffect(() => {
    if (!enrolling || enrollStartedRef.current) return;
    enrollStartedRef.current = true;
    void (async () => {
      const { data, errorMessage } = await mfaEnroll();
      if (errorMessage || !data) {
        enrollStartedRef.current = false; // allow a retry (e.g. on reload)
        setEnrollError(t('platformConsole.auth:mfaEnrollError'));
        return;
      }
      setEnroll({ secret: data.secret, otpauthUrl: data.otpauthUrl });
    })();
  }, [enrolling, t]);

  const onVerify = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setVerifyError(null);
    if (!CODE_RE.test(code)) {
      setVerifyError(t('platformConsole.auth:mfaCodeFormat'));
      return;
    }
    setVerifying(true);
    const { data, errorMessage } = await mfaVerify(code);
    setVerifying(false);
    if (errorMessage || !data || !data.ok) {
      setVerifyError(t('platformConsole.auth:mfaInvalidCode'));
      return;
    }
    // On first enrollment success the server flips mfa_enabled; reflect it.
    if (enrolling) {
      await refreshPlatformUser();
    }
    markMfaPassed();
  };

  return (
    <CenteredCanvas>
      <Card>
        <SectionTitle>
          {enrolling
            ? t('platformConsole.auth:mfaEnrollTitle')
            : t('platformConsole.auth:mfaRequiredTitle')}
        </SectionTitle>
        <p
          style={{
            margin: `0 0 ${theme.spacing.space[5]}px`,
            fontFamily: theme.typography.family.sans,
            fontSize: theme.typography.size.footnote,
            color: theme.color.textSecondary,
            lineHeight: theme.typography.lineHeight.normal,
          }}
        >
          {enrolling
            ? t('platformConsole.auth:mfaEnrollSubtitle')
            : t('platformConsole.auth:mfaRequiredSubtitle')}
        </p>

        {enrolling ? (
          enrollError ? (
            <Banner tone="danger" style={{ marginBottom: theme.spacing.space[5] }}>
              {enrollError}
            </Banner>
          ) : enroll ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing.space[5],
                marginBottom: theme.spacing.space[6],
              }}
            >
              {/* Scan path (Google Authenticator etc.). The QR encodes the same
                  otpauth:// URI and is generated locally — the secret never leaves
                  the browser. QRCodeSVG defaults to dark-on-white so it scans on
                  either theme; marginSize adds the required quiet zone. */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: theme.spacing.space[3],
                }}
              >
                <QRCodeSVG value={enroll.otpauthUrl} size={188} marginSize={3} />
                <span
                  style={{
                    fontFamily: theme.typography.family.sans,
                    fontSize: theme.typography.size.footnote,
                    color: theme.color.textSecondary,
                    textAlign: 'center',
                  }}
                >
                  {t('platformConsole.auth:mfaScanHint')}
                </span>
              </div>
              {/* Manual fallback (authenticators without a camera / paste). */}
              <CopyRow label={t('platformConsole.auth:mfaSecretLabel')} value={enroll.secret} />
              <CopyRow
                label={t('platformConsole.auth:mfaUriLabel')}
                value={enroll.otpauthUrl}
              />
              <Banner tone="info">{t('platformConsole.auth:mfaSecretHint')}</Banner>
            </div>
          ) : (
            <p
              style={{
                fontFamily: theme.typography.family.sans,
                fontSize: theme.typography.size.body,
                color: theme.color.textSecondary,
              }}
            >
              {t('common:loading')}
            </p>
          )
        ) : null}

        <form
          onSubmit={onVerify}
          style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.space[5] }}
        >
          <Field
            label={t('platformConsole.auth:mfaCodeLabel')}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder={t('platformConsole.auth:mfaCodePlaceholder')}
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            error={verifyError ?? undefined}
          />
          <Button type="submit" fullWidth disabled={verifying || (enrolling && !enroll)}>
            {verifying
              ? t('platformConsole.auth:mfaVerifying')
              : enrolling
                ? t('platformConsole.auth:mfaEnrollCta')
                : t('platformConsole.auth:mfaVerifyCta')}
          </Button>
        </form>
      </Card>
    </CenteredCanvas>
  );
}
